import { useState, useRef, useEffect } from "react";
import { Car } from "@/types/fleet";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { format, addDays } from "date-fns";
import { de } from "date-fns/locale";
import { Plus, Minus, MapPin, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { buildWhatsAppLink } from "@/utils/whatsapp";
import { trackSelectDays, trackClickWhatsApp } from "@/utils/analytics";
import { toast } from "sonner";

interface DASBookingModalProps {
  car: Car | null;
  isOpen: boolean;
  onClose: () => void;
}

export const DASBookingModal = ({ car, isOpen, onClose }: DASBookingModalProps) => {
  const [startDate, setStartDate] = useState<Date>();
  const [days, setDays] = useState<number>(0);
  const [time, setTime] = useState("flexibel");
  const [customerName, setCustomerName] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const scrollTop = useRef(0);

  // Pull-to-refresh functionality
  useEffect(() => {
    const sheetElement = sheetRef.current;
    if (!sheetElement) return;

    const handleTouchStart = (e: TouchEvent) => {
      const element = e.currentTarget as HTMLElement;
      touchStartY.current = e.touches[0].clientY;
      scrollTop.current = element.scrollTop;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const element = e.currentTarget as HTMLElement;
      const touchY = e.touches[0].clientY;
      const diff = touchY - touchStartY.current;

      // Only trigger pull-to-refresh if at the top of scroll and pulling down
      if (element.scrollTop === 0 && diff > 0) {
        const distance = Math.min(diff * 0.5, 80); // Max 80px pull
        setPullDistance(distance);
        
        // Prevent default scroll behavior
        if (distance > 20) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = () => {
      if (pullDistance > 60) {
        setIsRefreshing(true);
        // Reset form
        setTimeout(() => {
          setStartDate(undefined);
          setDays(0);
          setTime("flexibel");
          setCustomerName("");
          setPickupLocation("");
          setIsRefreshing(false);
          setPullDistance(0);
          toast.success("Formular zurückgesetzt");
        }, 500);
      } else {
        setPullDistance(0);
      }
    };

    sheetElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    sheetElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    sheetElement.addEventListener('touchend', handleTouchEnd);

    return () => {
      sheetElement.removeEventListener('touchstart', handleTouchStart);
      sheetElement.removeEventListener('touchmove', handleTouchMove);
      sheetElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pullDistance, isOpen]);

  if (!car) return null;

  const totalPrice = days > 0 ? car.price_per_day_chf * days : 0;
  const returnDate = startDate && days > 0 ? addDays(startDate, days) : null;

  const handleQuickDate = (daysFromNow: number) => {
    setStartDate(addDays(new Date(), daysFromNow));
    if (days === 0) {
      setDays(1);
    }
  };

  const handleDaysChange = (newDays: number) => {
    const validDays = Math.max(0, newDays);
    setDays(validDays);
    if (validDays > 0) {
      trackSelectDays(car.id, validDays);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation wird von Ihrem Browser nicht unterstützt");
      return;
    }

    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setIsLoadingLocation(false);
        // Format coordinates
        const coordsText = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        setPickupLocation(coordsText);
        toast.success("Standort erfolgreich ermittelt");
      },
      (error) => {
        setIsLoadingLocation(false);
        toast.error("Standort konnte nicht ermittelt werden");
        console.error("Geolocation error:", error);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleWhatsAppBooking = () => {
    // Check each field and show specific error messages
    const missingFields = [];
    
    if (!customerName.trim()) {
      missingFields.push("Name");
      toast.error("Bitte geben Sie Ihren Namen ein");
    }
    
    if (!pickupLocation.trim()) {
      missingFields.push("Abholort");
      toast.error("Bitte geben Sie den Abholort ein");
    }
    
    if (!startDate) {
      missingFields.push("Abholdatum");
      toast.error("Bitte wählen Sie ein Abholdatum");
    }
    
    // If any fields are missing, don't proceed
    if (missingFields.length > 0) return;

    trackClickWhatsApp(car.id);
    const link = buildWhatsAppLink({
      car,
      date: startDate,
      days,
      time,
      customerName: customerName.trim(),
      pickupLocation: pickupLocation.trim(),
    });

    window.open(link, '_blank');
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        ref={sheetRef}
        className="w-full sm:max-w-2xl overflow-y-auto [&>div]:!max-h-full" 
        style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
      >
        {/* Pull-to-refresh indicator */}
        <div 
          className="absolute top-0 left-0 right-0 flex items-center justify-center transition-all duration-300 pointer-events-none"
          style={{ 
            height: `${pullDistance}px`,
            opacity: pullDistance / 60,
            transform: `translateY(-${Math.max(0, 20 - pullDistance)}px)`
          }}
        >
          <RefreshCw 
            className={`h-5 w-5 text-gold ${isRefreshing ? 'animate-spin' : ''}`}
            style={{ transform: `rotate(${pullDistance * 3}deg)` }}
          />
        </div>

        <SheetHeader className="mb-6 flex-shrink-0" style={{ paddingTop: `${Math.max(0, pullDistance - 20)}px` }}>
          <SheetTitle className="text-2xl font-heading">
            {car.title} buchen
          </SheetTitle>
          <SheetDescription>
            Wählen Sie Ihre Mietdaten und wir verbinden Sie via WhatsApp
          </SheetDescription>
        </SheetHeader>
        
        <div className="space-y-6 pb-6">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Ihr Name *</Label>
            <Input
              id="name"
              placeholder="Max Mustermann"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </div>

          {/* Pickup Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Abholort *</Label>
            <div className="flex gap-2">
              <Input
                id="location"
                placeholder="Adresse oder Standort eingeben"
                value={pickupLocation}
                onChange={(e) => setPickupLocation(e.target.value)}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleUseMyLocation}
                disabled={isLoadingLocation}
                className="border-2 border-gold text-gold hover:bg-gold hover:text-white active:bg-gold active:text-white transition-colors duration-200 touch-manipulation"
                title="Mein Standort verwenden"
              >
                {isLoadingLocation ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <MapPin className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Tippen Sie auf <MapPin className="h-3 w-3 inline" /> um Ihren aktuellen Standort zu verwenden
            </p>
          </div>

          {/* Date Picker */}
          <div className="space-y-2">
            <Label htmlFor="date">Abholdatum *</Label>
            <div className="flex gap-2 mb-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-2 border-gold text-gold hover:bg-gold hover:text-white active:bg-gold active:text-white transition-colors duration-200 touch-manipulation"
                onClick={() => handleQuickDate(0)}
              >
                Heute
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-2 border-gold text-gold hover:bg-gold hover:text-white active:bg-gold active:text-white transition-colors duration-200 touch-manipulation"
                onClick={() => handleQuickDate(1)}
              >
                Morgen
              </Button>
            </div>
            <Input
              id="date"
              type="date"
              value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
              onChange={(e) => {
                const selectedDate = e.target.value ? new Date(e.target.value) : undefined;
                setStartDate(selectedDate);
                if (selectedDate && days === 0) {
                  setDays(1);
                }
              }}
              min={format(new Date(), "yyyy-MM-dd")}
            />
          </div>

          {/* Days */}
          <div className="space-y-2">
            <Label>Mietdauer (Tage)</Label>
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => handleDaysChange(days - 1)}
                disabled={days <= 0}
                className="border-2 border-gold text-gold hover:bg-gold hover:text-white active:bg-gold active:text-white transition-colors duration-200 touch-manipulation"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <div className="flex-1 text-center">
                <span className="text-2xl font-bold text-gold">{days}</span>
                <span className="text-sm text-gray-600 ml-2">
                  {days === 1 ? "Tag" : "Tage"}
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => handleDaysChange(days + 1)}
                className="border-2 border-gold text-gold hover:bg-gold hover:text-white active:bg-gold active:text-white transition-colors duration-200 touch-manipulation"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {days === 0 && (
              <p className="text-xs text-muted-foreground text-center">
                Wählen Sie die Anzahl der Tage oder fragen Sie nach Wochen-/Monatspreisen
              </p>
            )}
          </div>

          {/* Time */}
          <div className="space-y-2">
            <Label htmlFor="time">Abholzeit</Label>
            <Input
              id="time"
              placeholder="z.B. 10:00 Uhr"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>

          {/* Price Summary */}
          {days > 0 && car.price_per_day_chf > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Preis pro Tag:</span>
                <span className="font-medium">CHF {car.price_per_day_chf}.-</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Anzahl Tage:</span>
                <span className="font-medium">{days}</span>
              </div>
              {returnDate && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Rückgabe:</span>
                  <span className="font-medium">
                    {format(returnDate, "PPP", { locale: de })}
                  </span>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="font-semibold">Gesamtpreis:</span>
                  <span className="text-xl font-bold text-gold">
                    CHF {totalPrice}.-
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                + CHF {car.deposit_chf}.- Kaution (wird zurückerstattet)
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <Button
            className={cn(
              "w-full touch-manipulation transition-all duration-300",
              startDate && customerName.trim() && pickupLocation.trim()
                ? "bg-gold hover:bg-gold/90 active:bg-gold-hover text-white"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            )}
            onClick={handleWhatsAppBooking}
            disabled={!startDate || !customerName.trim() || !pickupLocation.trim()}
          >
            Auf WhatsApp senden
          </Button>

          {days === 0 && (
            <Button
              variant="outline"
              className="w-full border-2 border-gold text-gold hover:bg-gold hover:text-white active:bg-gold active:text-white transition-colors duration-200 touch-manipulation"
              onClick={() => {
                const message = `Hallo! Ich interessiere mich für ${car.title}. Können Sie mir bitte Ihre Wochen- und Monatspreise mitteilen?`;
                window.open(`https://wa.me/41797085008?text=${encodeURIComponent(message)}`, '_blank');
              }}
            >
               Nach Wochen-/Monatspreisen fragen
            </Button>
           )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
